"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Mail } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { ThemeToggle } from "@/lib/theme";

export default function SupportPage() {
  const [user, setUser] = useState<{ id: string; email: string; display_name?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeCard, setActiveCard] = useState<"contact" | "refund" | "bug" | null>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user: u } }) => {
      if (!u) { window.location.href = "/auth"; return; }
      supabase
        .from("users")
        .select("id, email, display_name")
        .eq("id", u.id)
        .single()
        .then(({ data }) => {
          setUser(data ?? { id: u.id, email: u.email ?? "" });
          setLoading(false);
        });
    });
  }, []);

  async function handleRefundSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/support/refund-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data.error ?? "Something went wrong. Please try again.");
      } else {
        setSubmitted(true);
      }
    } catch {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nav */}
      <nav className="bg-[var(--bg-card)] border-b border-gray-100 px-6 py-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link href="/" className="text-xl font-bold text-[var(--text-primary)]">FullPracticeTests</Link>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <ThemeToggle />
            <span className="hidden sm:block">{user?.display_name ?? user?.email}</span>
            <Link href="/dashboard" className="hover:text-gray-900">Dashboard</Link>
            <Link href="/settings" className="hover:text-gray-900">Settings</Link>
            <button
              onClick={async () => {
                const s = createClient();
                await s.auth.signOut();
                window.location.href = "/auth";
              }}
              className="hover:text-gray-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">How can we help?</h1>
        <p className="text-sm text-gray-500 mb-8">Choose an option below and we'll get back to you quickly.</p>

        {/* Cards */}
        <div className="grid gap-4">

          {/* Contact Support */}
          <div
            className={`bg-white rounded-xl border transition-colors cursor-pointer
              ${activeCard === "contact" ? "border-blue-300 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}
            onClick={() => setActiveCard(activeCard === "contact" ? null : "contact")}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <Mail className="w-5 h-5" strokeWidth={1.75} aria-hidden="true" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 text-sm">Contact Support</div>
                  <div className="text-xs text-gray-400 mt-0.5">Send us an email and we'll respond within 24 hours</div>
                </div>
              </div>
              <span className={`text-gray-400 transition-transform text-xs flex-shrink-0 ${activeCard === "contact" ? "rotate-90" : ""}`}>▶</span>
            </div>
            {activeCard === "contact" && (
              <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                <p className="text-sm text-gray-600 mb-3">
                  Email us directly and we'll get back to you as soon as possible.
                </p>
                <a
                  href="mailto:support@fullpracticetests.com"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  support@fullpracticetests.com
                </a>
              </div>
            )}
          </div>

          {/* Request a Refund */}
          <div
            className={`bg-white rounded-xl border transition-colors
              ${activeCard === "refund" ? "border-blue-300 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}
          >
            <div
              className="flex items-center justify-between px-5 py-4 cursor-pointer"
              onClick={() => {
                setActiveCard(activeCard === "refund" ? null : "refund");
                setSubmitted(false);
                setSubmitError(null);
                setReason("");
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500 text-base font-semibold flex-shrink-0">$</div>
                <div>
                  <div className="font-medium text-gray-900 text-sm">Request a Refund</div>
                  <div className="text-xs text-gray-400 mt-0.5">Submit a refund request for admin review</div>
                </div>
              </div>
              <span className={`text-gray-400 transition-transform text-xs flex-shrink-0 ${activeCard === "refund" ? "rotate-90" : ""}`}>▶</span>
            </div>
            {activeCard === "refund" && (
              <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                {submitted ? (
                  <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3 text-sm text-green-800">
                    Your refund request has been submitted. We'll review it within 24 hours and email you at{" "}
                    <span className="font-medium">{user?.email}</span>.
                  </div>
                ) : (
                  <form onSubmit={handleRefundSubmit} onClick={e => e.stopPropagation()}>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      Reason <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      placeholder="Please describe why you'd like a refund…"
                      required
                      rows={4}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    {submitError && (
                      <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                        {submitError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting || !reason.trim()}
                      className="mt-3 text-sm bg-[var(--text-primary)] text-[var(--bg-primary)] px-5 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-bold"
                    >
                      {submitting ? "Submitting…" : "Submit Refund Request"}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Report a Bug */}
          <div
            className={`bg-white rounded-xl border transition-colors cursor-pointer
              ${activeCard === "bug" ? "border-blue-300 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}
            onClick={() => setActiveCard(activeCard === "bug" ? null : "bug")}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center text-red-500 text-base font-semibold flex-shrink-0">!</div>
                <div>
                  <div className="font-medium text-gray-900 text-sm">Report a Bug</div>
                  <div className="text-xs text-gray-400 mt-0.5">Found something broken? Let us know</div>
                </div>
              </div>
              <span className={`text-gray-400 transition-transform text-xs flex-shrink-0 ${activeCard === "bug" ? "rotate-90" : ""}`}>▶</span>
            </div>
            {activeCard === "bug" && (
              <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                <p className="text-sm text-gray-600 mb-3">
                  Describe the bug and what you were doing when it occurred. We'll investigate right away.
                </p>
                <a
                  href="mailto:support@fullpracticetests.com?subject=Bug%20Report"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  Send bug report email
                </a>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
